import {Image as ChakraImage} from '@chakra-ui/react3'
import PropTypes from 'prop-types'
import React from 'react'
import DynamicImage from '../../../../components/dynamic-image'
import {breakpoints} from '../../../theme/breakpoints'
import useDisBaseLink from '../../../../hooks/useDisBaseLink'

/**
 * Renders an image with optional title.
 * Intended for use within the Leap components library.
 * Automatically converts Salesforce static URLs to DIS URLs for optimized image delivery.
 *
 * @component
 * @param {Object} props - Component properties.
 * @param {string} props.src - The image source URL.
 * @param {string} [props.disBaseLink] - DIS base link for image. If not provided, automatically generated from src.
 * @param {string} [props.title] - Optional title attribute for the image.
 * @param {string} [props.alt] - Alt text for accessibility.
 * @param {boolean} [props.preload=false] - Whether to preload the image. Only used if disBaseLink is provided or auto-generated.
 * @param {array|object} [props.widths] - Responsive widths for responsive images. Only used if disBaseLink is provided or auto-generated.
 * @param {('eager'|'lazy')} [props.loading='lazy'] - Loading strategy for the image - only use 'eager' for above-the-fold images.
 * @returns {JSX.Element} An image element.
 */

const Image = ({
    src,
    disBaseLink,
    title,
    alt,
    preload = false,
    widths,
    loading = 'lazy',
    draggable = false,
    fetchpriority,
    ...rest
}) => {
    // Automatically generate DIS base link from src if not provided
    const autoDisBaseLink = useDisBaseLink(src)
    const effectiveDisBaseLink = disBaseLink || autoDisBaseLink

    const imageProps = {
        width: '100%',
        height: '100%',
        ...rest,
        alt,
        title,
        loading,
        ...(fetchpriority && {fetchpriority})
    }

    if (effectiveDisBaseLink) {
        return (
            <DynamicImage
                src={`${effectiveDisBaseLink}[?sw={width}&q=60]`}
                imageProps={imageProps}
                preload={preload}
                widths={widths}
                breakpoints={breakpoints}
            />
        )
    }
    return <ChakraImage {...imageProps} src={src} draggable={draggable} />
}

Image.propTypes = {
    alt: PropTypes.string,
    disBaseLink: PropTypes.string,
    fetchpriority: PropTypes.oneOf(['high', 'low', 'auto']),
    loading: PropTypes.oneOf(['eager', 'lazy']),
    preload: PropTypes.bool,
    src: PropTypes.string.isRequired,
    title: PropTypes.string,
    widths: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
    draggable: PropTypes.bool
}

export default Image
